import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  UserCircle,
  Briefcase,
  LogOut,
  Star,
  User,
  CreditCard,
  Users, Video, UserCheck 
} from 'lucide-react';

import { useAuth } from '../../../context/AuthContext';
import './CastingDirectorSidebar.css';

const CastingDirectorSidebar = () => {

  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [showDrawer, setShowDrawer] = useState(false);
  const handleClose = () => setShowDrawer(false);
  const location = useLocation();

  const handleLogout = () => {
    logout();
    handleClose();
    navigate('/login');
  };

  // ✅ Client-side permission validation helper
  const hasPermission = (moduleKey, action = 'View') => {
    // CD (400) Bypass
    if (String(user?.roleCode || user?.role_code) === '400') {
      return true;
    }
    // Staff (900) check
    const perms = user?.permissions || [];
    return perms.includes(`${moduleKey}:${action}`);
  };

  // USER DATA
  const displayName = user?.full_name || user?.fullName || 'Staff Member';
  const displayRole = user?.role || (String(user?.roleCode || user?.role_code) === '900' ? 'Team Member' : 'Casting Director');
  const profileImageUrl = user?.profile_image_url || '';

  // FALLBACK AVATAR
  const defaultAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=d90429&color=fff&size=150`;

  const imageSource = profileImageUrl ? profileImageUrl : defaultAvatar;

  return (
    <div className="cd-sidebar py-4">

      {/* MAIN CARD */}
      <div className="cd-main-card mt-5">

        {/* PROFILE */}
        <div className="sidebar-profile text-center mb-4">

          <img
            src={imageSource}
            alt="Casting Director"
            className="td-profile-img"
            onError={(e) => {
              e.target.src = defaultAvatar;
            }}
          />

          <h4>{displayName}</h4>

          <p>{displayRole}</p>

        </div>


        {/* SIDEBAR MENU */}

        <div className="cd-sidebar-nav">

          {/* 1. Dashboard Link */}
          {hasPermission('Dashboard', 'View') && (
            <NavLink
              to="/dashboard/overview"
              className={({ isActive }) =>
                isActive ? "cd-menu-item active" : "cd-menu-item"
              }
            >
              <User size={18} /> Dashboard
            </NavLink>
          )}

          {/* 2. Profile Link (Always Visible) */}
          <NavLink
            to="/dashboard/profile"
            className={({ isActive }) =>
              isActive ? "cd-menu-item active" : "cd-menu-item"
            }
          >
            <UserCircle size={16} /> Profile
          </NavLink>

          {/* 3. Projects Link */}
          {hasPermission('Projects', 'View') && (
            <NavLink
              to="/dashboard/projects"
              className={({ isActive }) =>
                isActive ? "cd-menu-item active" : "cd-menu-item"
              }
            >
              <Briefcase size={18} /> Projects
            </NavLink>
          )}

          {/* 4. Manage Roles Link */}
            {hasPermission('Roles', 'View') && ( // ✅ Changed key to 'Roles'
            <NavLink
              to="/dashboard/roles"
              className={({ isActive }) =>
                isActive ? "cd-menu-item active" : "cd-menu-item"
              }
            >
              <Users size={18} /> Manage Roles
            </NavLink>
          )}
          {/* 5. Auditions Link */}
          {hasPermission('Auditions', 'View') && ( // ✅ Changed key to 'Auditions'
            <NavLink
              to="/dashboard/manage-applications"
              className={({ isActive }) =>
                isActive || location.pathname.includes('/candidate-profile') 
                    ? "cd-menu-item active" 
                    : "cd-menu-item"
              }
            >
              <Users size={16} /> Audition
            </NavLink>
          )}
          {/* 6. Reviews Link */}
          {hasPermission('Reviews', 'View') && ( // ✅ Changed key to 'Reviews'
            <NavLink
              to="/dashboard/casting-testimonials"
              className={({ isActive }) =>
                isActive ? "cd-menu-item active" : "cd-menu-item"
              }
            >
              <Star size={18} /> Reviews
            </NavLink>
          )}
          {/* 7. Meetings Link */}
          {hasPermission('Meetings', 'View') && ( // ✅ Changed key to 'Meetings'
            <NavLink
              to="/dashboard/meetings"
              className={({ isActive }) =>
                isActive ? "cd-menu-item active" : "cd-menu-item"
              }
            >
              <Video size={16} /> Meetings
            </NavLink>
          )}
          
          {/* 8. Team Members Link */}
          {hasPermission('Team', 'View') && (
            <NavLink 
              to="/dashboard/team-members" 
              className={({ isActive }) => 
                isActive ? "cd-menu-item active" : "cd-menu-item"
              }
            >
              <UserCheck size={16} /> Team Members
            </NavLink>
          )}

          {/* 9. Billing Link */}
          {hasPermission('Billing', 'View') && (
            <NavLink
              to="/dashboard/transaction-history"
              className={({ isActive }) =>
                isActive ? "cd-menu-item active" : "cd-menu-item"
              }
            >
              <CreditCard size={16} /> Billing
            </NavLink>
          )}

        </div>


        {/* LOGOUT */}

        <div
          className="logout mt-3 pt-3 border-top border-secondary d-flex align-items-center gap-2 text-danger"
          onClick={handleLogout}
          style={{ cursor: 'pointer' }}
        >
          <LogOut size={16} />
          <span className="fw-bold">Sign Out</span>
        </div>

      </div>

    </div>
  );
};

export default CastingDirectorSidebar;